'use client';

import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { useAuth } from '@/lib/auth';
import {
  resolvePostHogIdentityUpdate,
  type PostHogIdentity,
} from '@/lib/analytics/posthog-identity';
import { metaPixelTrackCompleteRegistrationOnce } from '@/lib/meta-pixel';
import { clearPostHogSupportIdentity } from '@/lib/posthog-support/client';
import {
  ensurePostHogBrowserInitialized,
  posthog,
  posthogDeploymentProperties,
  registerPostHogDeploymentProperties,
} from '../../lib/observability/posthog-browser-client';

ensurePostHogBrowserInitialized();

function PostHogIdentifier() {
  const { user, isLoading, isSignedOut } = useAuth();
  const previousIdentityRef = useRef<PostHogIdentity | null>(null);

  useEffect(() => {
    // Only proceed if PostHog is initialized
    if (!ensurePostHogBrowserInitialized()) {
      return;
    }

    const update = resolvePostHogIdentityUpdate({
      user,
      isLoading,
      isSignedOut,
      previous: previousIdentityRef.current,
      persistedUserId: posthog.get_property('$user_id'),
    });
    if (!update) return;
    try {
      if (update.reset) {
        clearPostHogSupportIdentity(posthog);
        posthog.reset();
        registerPostHogDeploymentProperties();
        previousIdentityRef.current = null;
      }
      if (update.identify && update.next) {
        posthog.identify(update.next.userId, {
          email: update.next.email,
          name: update.next.name,
          platform_account:
            posthogDeploymentProperties.deployment_environment === 'production',
          last_environment: posthogDeploymentProperties.environment,
        });
      }
      if (update.registration && update.next) {
        metaPixelTrackCompleteRegistrationOnce(update.next.userId);
      }
      previousIdentityRef.current = update.next;
    } catch (error) {
      console.error('[PostHog] Error synchronizing user:', error);
    }
  }, [user, isLoading, isSignedOut]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <PHProvider client={posthog}>
      <PostHogIdentifier />
      {children}
    </PHProvider>
  );
}
